import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAnglesLeft,
  faAnglesRight,
  faChartColumn,
  faClipboardCheck,
  faListCheck,
  faRotate,
  faSearch,
  faSpinner,
  faTicket,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../../components/Ui_Element/Breadcrumb";
import { getCurrentUserIdentity } from "../../Tickets/ticketUtils";
import { fetchEmployees, getMyTaskHistoryPaged } from "../taskService";
import {
  formatTaskDate,
  getTaskHistoryActionClasses,
  getTaskPriorityClasses,
  getTaskStatusClasses,
} from "../taskUtils";

const STATUS_FILTERS = ["All", "Pending", "In Progress", "Waiting", "Forwarded", "Completed", "Cancelled"];
const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];
const DEFAULT_PAGE_SIZE = 10;
const MAX_VISIBLE_PAGES = 9;

export default function MyTasksPage() {
  const location = useLocation();
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedUserId = searchParams.get("userId") || identity.userId || "";

  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [assigneeFilter, setAssigneeFilter] = useState("All");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageMeta, setPageMeta] = useState({
    totalCount: 0,
    totalPages: 0,
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPageNumber(1);
      setSearchText(searchInput.trim());
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    let active = true;

    const loadEmployees = async () => {
      try {
        const data = await fetchEmployees();
        if (active) setEmployees(data);
      } catch {
        if (active) setEmployees([]);
      }
    };

    loadEmployees();

    return () => {
      active = false;
    };
  }, []);

  const loadItems = React.useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await getMyTaskHistoryPaged({
        userId: requestedUserId,
        pageNumber,
        pageSize,
        searchText,
        status: statusFilter,
        assignedEmployeeId: assigneeFilter === "All" ? 0 : Number(assigneeFilter),
      });

      setItems(data.items || []);
      setPageMeta({
        totalCount: data.totalCount || 0,
        totalPages: data.totalPages || 0,
        pageNumber: data.pageNumber || 1,
        pageSize: data.pageSize || DEFAULT_PAGE_SIZE,
        hasNextPage: Boolean(data.hasNextPage),
        hasPreviousPage: Boolean(data.hasPreviousPage),
      });
    } catch (loadError) {
      setItems([]);
      setPageMeta((current) => ({
        ...current,
        totalCount: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      }));
      setError(loadError?.message || "My task history could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [requestedUserId, pageNumber, pageSize, searchText, statusFilter, assigneeFilter]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const paginationNumbers = useMemo(
    () => buildVisiblePageNumbers(pageMeta.pageNumber, pageMeta.totalPages, MAX_VISIBLE_PAGES),
    [pageMeta.pageNumber, pageMeta.totalPages]
  );

  const rangeLabel = useMemo(() => {
    if (!pageMeta.totalCount || !items.length) return "Showing 0 items";

    const start = (pageMeta.pageNumber - 1) * pageMeta.pageSize + 1;
    const end = start + items.length - 1;
    return `Showing ${start}-${end} of ${pageMeta.totalCount}`;
  }, [items.length, pageMeta.pageNumber, pageMeta.pageSize, pageMeta.totalCount]);

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <Breadcrumb pageName="Task History" currentPage="Task History" />

      <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.18),_transparent_30%),linear-gradient(135deg,#0f172a,#1e293b_58%,#334155)] px-5 py-6 text-white md:px-7 md:py-8">
          <div className="absolute right-0 top-0 h-36 w-36 translate-x-10 -translate-y-8 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-100">
                <FontAwesomeIcon icon={faClipboardCheck} />
                Support Task History
              </div>
              <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-200">
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">Logged in as {identity.fullName || "User"}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">User ID: {requestedUserId || "Unavailable"}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">Server-side paging enabled</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={requestedUserId ? `/task-report?userId=${encodeURIComponent(requestedUserId)}` : "/task-report"}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                <FontAwesomeIcon icon={faChartColumn} />
                Task Report
              </Link>
              <button
                type="button"
                onClick={loadItems}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="p-5 md:p-6">
          <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="relative min-w-[220px]">
                <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search task, ticket, actor, or owner"
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-slate-400"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setPageNumber(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400"
              >
                {STATUS_FILTERS.map((status) => (
                  <option key={status} value={status}>
                    {status === "All" ? "All Status" : status}
                  </option>
                ))}
              </select>

              <select
                value={assigneeFilter}
                onChange={(event) => {
                  setAssigneeFilter(event.target.value);
                  setPageNumber(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400"
              >
                <option value="All">All Assignees</option>
                {employees.map((employee) => (
                  <option key={employee.id || employee.userId} value={employee.id}>
                    {employee.fullName} {employee.workEmail ? `- ${employee.workEmail}` : ""}
                  </option>
                ))}
              </select>

              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value) || DEFAULT_PAGE_SIZE);
                  setPageNumber(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400"
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    Page Size: {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="text-sm font-medium text-slate-500">{rangeLabel}</div>
          </div>

          {error ? (
            <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
          ) : null}

          {loading ? (
            <div className="rounded-2xl border border-dashed border-slate-300 px-6 py-14 text-center text-sm text-slate-500">
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Loading task history...
            </div>
          ) : items.length ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 bg-white text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                        <th className="px-4 py-3">Task</th>
                        <th className="px-4 py-3">Ticket</th>
                        <th className="px-4 py-3">Assignee</th>
                        <th className="px-4 py-3">Priority</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Due</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map((item) => {
                        const movementLabel =
                          item.fromAssignedEmployeeName && item.toAssignedEmployeeName && item.fromAssignedEmployeeName !== item.toAssignedEmployeeName
                            ? `${item.fromAssignedEmployeeName} -> ${item.toAssignedEmployeeName}`
                            : item.toAssignedEmployeeName || item.assignedEmployeeName || "Unassigned";

                        return (
                          <tr key={`${item.historyId}-${item.taskId}`} className="align-top hover:bg-slate-50/70">
                            <td className="px-4 py-4">
                              <div className="font-semibold text-slate-900">{item.taskTitle || "Untitled task"}</div>
                              <div className="mt-1 line-clamp-2 max-w-md text-sm text-slate-500">{item.taskDescription || "No description"}</div>
                              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                                <span>
                                  {item.actionLabel} by {item.actorName || "Unknown"} on {formatTaskDate(item.createdAt, true)}
                                </span>
                                <span className={`rounded-full px-2.5 py-1 font-semibold ${getTaskHistoryActionClasses(item.actionType)}`}>
                                  {item.actionLabel}
                                </span>
                              </div>
                              {item.note ? <div className="mt-2 text-xs text-slate-500">{item.note}</div> : null}
                            </td>
                            <td className="px-4 py-4">
                              {item.ticketId ? (
                              <Link
                                to={`/tickets/${item.ticketId}`}
                                state={{ backTo: `${location.pathname}${location.search}`, viewOnly: true }}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
                              >
                                  <FontAwesomeIcon icon={faTicket} />
                                  {item.ticketNumber || "Open Ticket"}
                                </Link>
                              ) : (
                                <span className="text-slate-400">Manual task</span>
                              )}
                              <div className="mt-2 text-sm text-slate-500">{item.ticketSubject || "No linked ticket subject"}</div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="font-medium text-slate-900">{item.assignedEmployeeName || "Unassigned"}</div>
                              <div className="mt-1 text-xs text-slate-500">{item.assignedEmployeeEmail || item.assignedUserId || "No email"}</div>
                              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">
                                  History owner
                                </span>
                                <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 font-semibold text-slate-600">
                                  {movementLabel}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getTaskPriorityClasses(item.taskPriority)}`}>
                                {item.taskPriority}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <div className="space-y-2">
                                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getTaskStatusClasses(item.historyStatus)}`}>
                                  {item.historyStatus}
                                </span>
                                <div className="text-xs text-slate-500">Task status: {item.taskStatus || "Pending"}</div>
                                <div className="text-xs text-slate-500">Task progress: {item.taskProgressPercent || 0}%</div>
                                <div className="text-xs text-slate-500">History progress: {item.historyProgressPercent || 0}%</div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="font-medium text-slate-900">{formatTaskDate(item.taskDueDate)}</div>
                              <div className="mt-1 text-xs text-slate-500">History {formatTaskDate(item.createdAt, true)}</div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex justify-end gap-2">
                                {item.ticketId ? (
                                <Link
                                  to={`/tickets/${item.ticketId}`}
                                  state={{ backTo: `${location.pathname}${location.search}`, viewOnly: true }}
                                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                                >
                                    Ticket
                                  </Link>
                                ) : null}
                                <Link
                                  to={requestedUserId ? `/my-tasks?userId=${encodeURIComponent(requestedUserId)}` : "/my-tasks"}
                                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                                >
                                  Task Desk
                                </Link>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-4 border-t border-slate-200 pt-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="text-sm text-slate-500">{rangeLabel}</div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPageNumber(1)}
                    disabled={!pageMeta.hasPreviousPage}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faAnglesLeft} />
                  </button>
                  {paginationNumbers.map((page) => (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setPageNumber(page)}
                      className={`inline-flex h-10 min-w-10 items-center justify-center rounded-full border px-3 text-sm font-semibold transition ${
                        page === pageMeta.pageNumber
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPageNumber(pageMeta.totalPages || 1)}
                    disabled={!pageMeta.hasNextPage}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faAnglesRight} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white">
                <FontAwesomeIcon icon={faListCheck} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">No task history found</h3>
              <p className="mt-2 text-sm text-slate-500">No SupportTaskHistory record matched this user and filter yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function buildVisiblePageNumbers(currentPage, totalPages, maxVisiblePages) {
  if (!totalPages) return [];

  const safeMax = Math.max(1, maxVisiblePages);
  let startPage = Math.max(1, currentPage - Math.floor(safeMax / 2));
  let endPage = startPage + safeMax - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - safeMax + 1);
  }

  const pages = [];
  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(page);
  }

  return pages;
}
